import React, { useEffect, useState } from 'react';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import { SUBMIT_FEATURE_REQUEST_AND_BUG_URL } from 'src/config/ExternalLinks';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import type { TaskConflict } from 'src/services/jmr/types';
import type { Modification } from 'src/services/modification/types';
import { setJmrResults } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';
import { isCanadianSite } from 'src/utils/helpers';
import { createDataCyForJob, hasJobBeenModified, type TableDataRow } from 'src/utils/jmrUtils';
import { getRequirementColor, getRequirementQuantity } from 'src/utils/placementSelectionUtils';

import {
  applyModificationsToRow,
  applyModificationsToShift,
  areModificationsValid,
  buildConflictsWithAppliedModifications,
  getModificationKey,
  hasSelectedModification,
} from './applyModificationsUtils';

export type CheckboxState = Record<string, boolean>;
export type FreeFormTextState = Record<string, string>;

interface ApplyModificationsModalProps {
  jobToModify: TableDataRow | null;
  shiftToModify: string | null;
  isOpen: boolean;
  exceededRestrictionIds: Set<number>;
  onClose: () => void;
  focusSelection: (resultId: string) => void;
}
const ApplyModificationsModal = ({
  jobToModify,
  shiftToModify,
  isOpen,
  exceededRestrictionIds,
  onClose,
  focusSelection,
}: ApplyModificationsModalProps) => {
  const [checkboxState, setCheckboxState] = useState<CheckboxState>({});
  const [freeFormTextState, setFreeFormTextState] = useState<FreeFormTextState>({});
  const { jmrResults, backgroundFormData } = useAppSelector((state) => state.newJmrPage);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (jobToModify?.conflicts?.length) {
      const newCheckboxState: CheckboxState = {};
      const newFreeFormTextState: FreeFormTextState = {};
      jobToModify.conflicts.forEach((conflict) =>
        conflict.appliedModifications?.forEach((appliedModifications) => {
          const key = getModificationKey(conflict.taskId, appliedModifications.modificationId);
          newCheckboxState[key] = true;
          if (appliedModifications.freeFormText) newFreeFormTextState[key] = appliedModifications.freeFormText;
        }),
      );
      setCheckboxState(newCheckboxState);
      setFreeFormTextState(newFreeFormTextState);
    }
  }, [jobToModify?.conflicts]);

  const isValid = areModificationsValid(jobToModify, checkboxState, freeFormTextState);
  const show180DayLimit = !isCanadianSite(backgroundFormData.site.countryId);

  const getModifiedConflicts = (): TaskConflict[] =>
    buildConflictsWithAppliedModifications(jobToModify!.conflicts, checkboxState, freeFormTextState);

  const saveModifications = (modifiedConflicts: TaskConflict[], isChecked: boolean) => {
    const shiftLength = backgroundFormData.shiftLength;
    const modifiedJmrResults = jmrResults.map((row) => {
      if (jobToModify?.id !== row.id) return row;
      if (shiftToModify && row.shifts.length > 1) {
        return applyModificationsToShift(row, shiftToModify, modifiedConflicts, isChecked, shiftLength);
      }
      return applyModificationsToRow(row, modifiedConflicts, isChecked, shiftLength);
    });
    dispatch(setJmrResults(modifiedJmrResults));
  };

  const onSaveAndApply = (modifiedConflicts: TaskConflict[]) => {
    saveModifications(modifiedConflicts, true);
    focusSelection(shiftToModify ?? jobToModify!.id);
    dispatch(
      createToast({
        type: 'success',
        message: 'Job was modified and selected',
        timeout: TOAST_TIMEOUT,
      }),
    );
    onClose();
  };

  const onRemoveModifications = () => {
    saveModifications(
      jobToModify!.conflicts.map((conflict) => ({ ...conflict, appliedModifications: [] })),
      false,
    );

    dispatch(
      createToast({
        type: 'success',
        message: 'All modifications were removed and job was unselected',
        timeout: TOAST_TIMEOUT,
      }),
    );
    onClose();
  };

  return (
    <Modal
      title={`Modify: ${jobToModify?.name}`}
      open={isOpen}
      onClose={onClose}
      closeLabel="Close"
      aria-describedby="modal-description"
      width="700px"
      scrollContainer="modal"
    >
      <Text type="b300" id="modal-description">
        Review the available modifications carefully to ensure they are appropriate for the specific role.{' '}
        <strong>If approved, partner with Operations to implement and purchase any items needed. </strong>
        {show180DayLimit && 'Modifying a job will apply a 180 day limit to the job placement. '}
        Missing modification? Please submit a{' '}
        <Link type="secondary" target="_blank" href={SUBMIT_FEATURE_REQUEST_AND_BUG_URL}>
          SIM ticket
        </Link>
        .
      </Text>
      {!!jobToModify && (
        <Column spacingInset="400">
          {jobToModify.conflicts.map((conflict) => (
            <ConflictModificationSelection
              key={conflict.taskId}
              conflict={conflict}
              exceededRestrictionIds={exceededRestrictionIds}
              jobToModify={jobToModify}
              isModified={hasSelectedModification(conflict, checkboxState)}
            >
              {conflict.availableModifications?.map((modification, i) => {
                const key = getModificationKey(conflict.taskId, modification.id);
                return (
                  <ConflictModificationSelectionOption
                    key={modification.id}
                    modification={modification}
                    cyLabel={`${createDataCyForJob(jobToModify.name)}-modification-${i}`}
                    isSelected={!!checkboxState[key]}
                    freeFormText={freeFormTextState[key] ?? ''}
                    onToggle={(isChecked: boolean) => {
                      setCheckboxState({ ...checkboxState, [key]: isChecked });
                      if (!isChecked) {
                        const { [key]: _, ...rest } = freeFormTextState;
                        setFreeFormTextState(rest);
                      }
                    }}
                    onFreeFormTextChange={(value: string) =>
                      setFreeFormTextState({ ...freeFormTextState, [key]: value.slice(0, 250) })
                    }
                  />
                );
              })}
            </ConflictModificationSelection>
          ))}
        </Column>
      )}
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          {!!jobToModify && hasJobBeenModified(jobToModify.conflicts) && (
            <Button type="tertiary" onClick={onRemoveModifications}>
              Remove Modification
            </Button>
          )}
          <Button type="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-cy={'new-jmr__results__apply-modifications'}
            type="primary"
            disabled={!isValid}
            onClick={() => onSaveAndApply(getModifiedConflicts())}
          >
            Apply & Select
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

const ConflictModificationSelection = ({
  conflict,
  exceededRestrictionIds,
  jobToModify,
  isModified,
  children,
}: {
  conflict: TaskConflict;
  exceededRestrictionIds: Set<number>;
  jobToModify: TableDataRow;
  isModified: boolean;
  children: React.ReactNode;
}) => (
  <Box spacingInset="400" type="outline">
    <Row alignmentHorizontal="justify">
      <Row spacing="200">
        <Text color={getRequirementColor(conflict, exceededRestrictionIds)} type="h100">
          Requirement: {conflict.taskName}
        </Text>
        {conflict.requirementProportion && (
          <Text color={getRequirementColor(conflict, exceededRestrictionIds)}>
            ({getRequirementQuantity(jobToModify, conflict)} of shift)
          </Text>
        )}
      </Row>
      <Tag type={isModified ? 'success' : 'warning'}>{isModified ? 'Modified' : 'Conflict'}</Tag>
    </Row>
    <Text>
      <strong>Conflict: </strong>
      {`Associate cannot ${conflict.taskName}${conflict.restrictedValue ? ` (${conflict.restrictedValue} Hours)` : ''}`}
    </Text>
    <Column spacing="200" spacingInset="400 none none none">
      <Text type="h100">Select a Modification</Text>
      {children}
    </Column>
  </Box>
);

const ConflictModificationSelectionOption = ({
  modification,
  cyLabel,
  isSelected,
  freeFormText,
  onToggle,
  onFreeFormTextChange,
}: {
  modification: Modification;
  cyLabel: string;
  isSelected: boolean;
  freeFormText: string;
  onToggle: (isChecked: boolean) => void;
  onFreeFormTextChange: (value: string) => void;
}) => {
  const { name: modificationName, isFreeFormTextAllowed } = modification;

  return (
    <Column>
      <Row>
        <Checkbox checked={isSelected} aria-label={modificationName} onChange={onToggle} data-cy={cyLabel} />
        <Text type="b300">{modificationName}</Text>
      </Row>
      {isSelected && isFreeFormTextAllowed ? (
        <Column spacing="100">
          <Text>
            Enter a proposed modification (required). <i>Do not share sensitive information</i>
          </Text>
          <Textarea
            value={freeFormText}
            onChange={onFreeFormTextChange}
            placeholder="Enter modification (required)..."
            constraintText={`${freeFormText.length} of 250 Characters`}
          />
        </Column>
      ) : null}
    </Column>
  );
};

export default ApplyModificationsModal;
